import React, { useState } from 'react';
import useSWR from 'swr';
import api from '../services/api';

interface DayData {
  exercise: string;
  mode_weight_kg: number;
  total_sets: number;
}

interface CalendarData {
  [date: string]: DayData[];
}

function VolumeSummary({ data }: { data: any }) {
  // 后端新版本返回 total_volume / muscle_volume；旧版本回退由 sets 现算总量（部位分解不可用）
  const totalVolume = data.total_volume !== undefined
    ? data.total_volume
    : (data.exercises || []).reduce(
        (sum: number, ex: any) => sum + (ex.sets || []).reduce(
          (s: number, set: any) => s + (set.weight_kg || 0) * (set.reps || 0), 0
        ), 0
      );
  const muscleVolumes: Record<string, number> = data.muscle_volume || {};

  if (!totalVolume && Object.keys(muscleVolumes).length === 0) return null;

  return (
    <div className="cal-volume-summary">
      <span className="cal-volume-total">总容量 {totalVolume} kg</span>
      {Object.entries(muscleVolumes).map(([muscle, vol]) => (
        <span key={muscle} className="cal-muscle-tag">{muscle} {vol}kg</span>
      ))}
    </div>
  );
}

export default function CalendarWidget() {
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);

  // SWR: 月历数据（有缓存立即显示；切月时 keepPreviousData 保留上一月，新数据到了再替换 → 零闪烁）
  const { data: calendar, error: calendarError } = useSWR<CalendarData>(
    ['calendar', year, month],
    () => api.getCalendar(year, month).then(r => r.data.data || {}),
    { keepPreviousData: true }
  );

  // SWR: 选中日期的训练详情（每个日期独立缓存，回看秒开）
  const { data: selectedData, error: dayError } = useSWR<any>(
    selectedDate ? ['training-day', selectedDate] : null,
    () => api.getTrainingDay(selectedDate as string).then(r => r.data.data)
  );

  const prevMonth = () => {
    if (month === 1) { setYear(y => y - 1); setMonth(12); }
    else setMonth(m => m - 1);
    setSelectedDate(null);
  };

  const nextMonth = () => {
    if (month === 12) { setYear(y => y + 1); setMonth(1); }
    else setMonth(m => m + 1);
    setSelectedDate(null);
  };

  const handleDayClick = (dateStr: string) => {
    if (!calendar || !calendar[dateStr]) return;
    setSelectedDate(dateStr);
  };

  const cal = calendar || {};

  const daysInMonth = new Date(year, month, 0).getDate();
  const firstDayOfWeek = new Date(year, month - 1, 1).getDay(); // 0=Sun

  const weekDays = ['日', '一', '二', '三', '四', '五', '六'];
  const monthNames = ['1月', '2月', '3月', '4月', '5月', '6月', '7月', '8月', '9月', '10月', '11月', '12月'];

  // Build calendar grid
  const cells: (number | null)[] = [];
  for (let i = 0; i < firstDayOfWeek; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const rows: (number | null)[][] = [];
  for (let i = 0; i < cells.length; i += 7) {
    rows.push(cells.slice(i, i + 7));
  }

  return (
    <div className="calendar-widget">
      <div className="calendar-header">
        <button className="cal-nav-btn" onClick={prevMonth}>‹</button>
        <span className="cal-title">{year}年{monthNames[month - 1]}</span>
        <button className="cal-nav-btn" onClick={nextMonth}>›</button>
      </div>

      {calendar === undefined && !calendarError && <div className="cal-loading">加载中...</div>}
      {calendarError && <div className="cal-loading">日历加载失败</div>}

      <div className="calendar-grid">
        {weekDays.map(w => (
          <div key={w} className="cal-weekday">{w}</div>
        ))}
        {rows.map((row, ri) => (
          <React.Fragment key={ri}>
            {row.map((day, ci) => {
              if (day === null) return <div key={ci} className="cal-cell empty" />;
              const dateStr = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
              const hasTraining = !!cal[dateStr];
              const isToday = dateStr === `${today.getFullYear()}-${String(today.getMonth()+1).padStart(2,'0')}-${String(today.getDate()).padStart(2,'0')}`;
              const isSelected = dateStr === selectedDate;

              return (
                <div
                  key={ci}
                  className={`cal-cell ${hasTraining ? 'has-training' : ''} ${isToday ? 'today' : ''} ${isSelected ? 'selected' : ''}`}
                  onClick={() => handleDayClick(dateStr)}
                >
                  <span className="cal-day-num">{day}</span>
                  {hasTraining && (
                    <div className="cal-day-summary">
                      {cal[dateStr].slice(0, 2).map((ex, i) => (
                        <span key={i} className="cal-ex-tag">{ex.exercise}</span>
                      ))}
                      {cal[dateStr].length > 2 && (
                        <span className="cal-ex-more">+{cal[dateStr].length - 2}</span>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </React.Fragment>
        ))}
      </div>

      {/* Day detail panel */}
      {selectedDate && (
        <div className="cal-detail">
          <h4>{selectedDate} 训练详情</h4>
          {dayError ? (
            <p className="cal-no-data">加载失败</p>
          ) : !selectedData ? (
            <p className="cal-no-data">加载中...</p>
          ) : selectedData.exercises && selectedData.exercises.length > 0 ? (
            <div className="cal-detail-exercises">
              {selectedData.exercises.map((ex: any, i: number) => {
                // Calculate mode weight
                const weights = ex.sets.map((s: any) => s.weight_kg);
                const weightCounts: Record<number, number> = {};
                weights.forEach((w: number) => { weightCounts[w] = (weightCounts[w] || 0) + 1; });
                const modeWeight = Object.entries(weightCounts).sort((a, b) => b[1] - a[1])[0][0];

                return (
                  <div key={i} className="cal-detail-exercise">
                    <span className="cal-ex-name">{ex.name}</span>
                    <span className="cal-ex-mode">众数 {modeWeight}kg</span>
                    <span className="cal-ex-sets">
                      {ex.sets.map((s: any, j: number) => (
                        <span key={j} className="cal-set-tag">{s.weight_kg}kg×{s.reps}</span>
                      ))}
                    </span>
                  </div>
                );
              })}
              <VolumeSummary data={selectedData} />
            </div>
          ) : (
            <p className="cal-no-data">休息日</p>
          )}
        </div>
      )}
    </div>
  );
}
